/**
 * Endoscopy labelling taxonomy — colonoscopy + upper-GI (EGD).
 *
 * Drives the two-tier labelling on still images:
 *   · IMAGE label  — what the whole frame shows: an anatomical
 *                    LANDMARK or a LESION (+ a free-text custom).
 *   · ANNOTATION   — a region tool (segmentation / bounding box /
 *                    measurement) tagged with a LESION class
 *                    (+ free-text custom).
 *
 * The lists below are grounded in the standard endoscopic
 * classification systems — see docs/development/endoscopy-lesion-
 * taxonomy.md for the per-item rationale + sources (Paris, NICE,
 * JNET, Kudo, Forrest, Los Angeles, Prague, Mayo). Where a finding
 * has a recognised grading scheme, it is offered as `grades` so the
 * reader can record severity/morphology without free text.
 *
 * This is a controlled vocabulary an organisation can extend; the IDs
 * are stable so stored labels survive a display-name change.
 */

export type Tier = "landmark" | "lesion";
export type Procedure = "colonoscopy" | "egd";

export interface Grade { id: string; name: string }

export interface Taxon {
  id:         string;          // stable id, e.g. "col.polyp"
  name:       string;          // display
  tier:       Tier;
  procedures: Procedure[];     // which exam(s) this applies to
  color:      string;          // stroke/fill colour
  grades?:    Grade[];         // optional severity / morphology scheme
  note?:      string;          // short clinician hint
}

// ---- shared grading schemes -------------------------------------------------

/** Paris endoscopic classification of superficial neoplastic lesions. */
const PARIS: Grade[] = [
  { id: "0-Ip",  name: "0-Ip pedunculated" },
  { id: "0-Isp", name: "0-Isp sub-pedunculated" },
  { id: "0-Is",  name: "0-Is sessile" },
  { id: "0-IIa", name: "0-IIa flat elevated" },
  { id: "0-IIb", name: "0-IIb flat" },
  { id: "0-IIc", name: "0-IIc flat depressed" },
  { id: "0-III", name: "0-III excavated" },
];

/** NICE optical-diagnosis (NBI, no magnification) for colorectal polyps. */
const NICE: Grade[] = [
  { id: "nice1", name: "NICE 1 (hyperplastic)" },
  { id: "nice2", name: "NICE 2 (adenoma)" },
  { id: "nice3", name: "NICE 3 (deep submucosal cancer)" },
];

/** JNET (magnifying NBI) for colorectal lesions. */
const JNET: Grade[] = [
  { id: "jnet1",  name: "JNET 1 (hyperplastic/SSL)" },
  { id: "jnet2a", name: "JNET 2A (low-grade adenoma)" },
  { id: "jnet2b", name: "JNET 2B (high-grade / shallow SM)" },
  { id: "jnet3",  name: "JNET 3 (deep SM invasion)" },
];

/** Forrest classification of peptic-ulcer bleeding stigmata. */
const FORREST: Grade[] = [
  { id: "Ia",  name: "Ia spurting" },
  { id: "Ib",  name: "Ib oozing" },
  { id: "IIa", name: "IIa visible vessel" },
  { id: "IIb", name: "IIb adherent clot" },
  { id: "IIc", name: "IIc flat pigmented spot" },
  { id: "III", name: "III clean base" },
];

/** Los Angeles classification of reflux esophagitis. */
const LA: Grade[] = [
  { id: "A", name: "LA grade A" },
  { id: "B", name: "LA grade B" },
  { id: "C", name: "LA grade C" },
  { id: "D", name: "LA grade D" },
];

/** Mayo endoscopic subscore for ulcerative colitis. */
const MAYO: Grade[] = [
  { id: "0", name: "Mayo 0 (normal)" },
  { id: "1", name: "Mayo 1 (mild)" },
  { id: "2", name: "Mayo 2 (moderate)" },
  { id: "3", name: "Mayo 3 (severe)" },
];

// ---- colonoscopy ------------------------------------------------------------

const COLON_LANDMARKS: Taxon[] = [
  ["col.anorectum",       "Anorectal junction"],
  ["col.rectum",          "Rectum"],
  ["col.sigmoid",         "Sigmoid colon"],
  ["col.descending",      "Descending colon"],
  ["col.splenic_flexure", "Splenic flexure"],
  ["col.transverse",      "Transverse colon"],
  ["col.hepatic_flexure", "Hepatic flexure"],
  ["col.ascending",       "Ascending colon"],
  ["col.cecum",           "Cecum"],
  ["col.ic_valve",        "Ileocecal valve"],
  ["col.appendiceal_orifice", "Appendiceal orifice"],
  ["col.terminal_ileum",  "Terminal ileum"],
].map(([id, name]) => ({ id, name, tier: "landmark", procedures: ["colonoscopy"], color: "#3B82F6" } as Taxon));

const COLON_LESIONS: Taxon[] = [
  { id: "col.polyp",  name: "Polyp",                 tier: "lesion", procedures: ["colonoscopy"], color: "#EF4444", grades: PARIS, note: "morphology = Paris; optical dx via NICE/JNET" },
  { id: "col.adenoma", name: "Adenoma",              tier: "lesion", procedures: ["colonoscopy"], color: "#EF4444", grades: NICE },
  { id: "col.ssl",    name: "Sessile serrated lesion", tier: "lesion", procedures: ["colonoscopy"], color: "#F59E0B" },
  { id: "col.cancer", name: "Colorectal cancer",     tier: "lesion", procedures: ["colonoscopy"], color: "#B91C1C", grades: JNET },
  { id: "col.ulcer",  name: "Ulcer",                 tier: "lesion", procedures: ["colonoscopy"], color: "#DC2626" },
  { id: "col.colitis", name: "Colitis / IBD",        tier: "lesion", procedures: ["colonoscopy"], color: "#F97316", grades: MAYO, note: "UC severity = Mayo endoscopic subscore" },
  { id: "col.diverticulosis", name: "Diverticulosis", tier: "lesion", procedures: ["colonoscopy"], color: "#A855F7" },
  { id: "col.angioectasia", name: "Angioectasia",    tier: "lesion", procedures: ["colonoscopy"], color: "#EC4899" },
  { id: "col.erythema", name: "Erythema / erosion",  tier: "lesion", procedures: ["colonoscopy"], color: "#FB7185" },
  { id: "col.bleeding", name: "Active bleeding",     tier: "lesion", procedures: ["colonoscopy"], color: "#991B1B" },
  { id: "col.hemorrhoids", name: "Internal hemorrhoids", tier: "lesion", procedures: ["colonoscopy"], color: "#9333EA" },
  { id: "col.melanosis", name: "Melanosis coli",     tier: "lesion", procedures: ["colonoscopy"], color: "#78716C" },
];

// ---- upper GI / EGD ---------------------------------------------------------

const EGD_LANDMARKS: Taxon[] = [
  ["egd.hypopharynx", "Hypopharynx / vocal cord"],
  ["egd.ues",        "Upper esophageal sphincter"],
  ["egd.esophagus",  "Esophagus"],
  ["egd.gej",        "Z-line / GE junction"],
  ["egd.cardia",     "Cardia"],
  ["egd.fundus",     "Fundus"],
  ["egd.body",       "Gastric body"],
  ["egd.incisura",   "Incisura angularis"],
  ["egd.antrum",     "Antrum"],
  ["egd.pylorus",    "Pylorus"],
  ["egd.bulb",       "Duodenal bulb (D1)"],
  ["egd.d2",         "Second part of duodenum (D2)"],
].map(([id, name]) => ({ id, name, tier: "landmark", procedures: ["egd"], color: "#2563EB" } as Taxon));

const EGD_LESIONS: Taxon[] = [
  { id: "egd.esophagitis", name: "Reflux esophagitis", tier: "lesion", procedures: ["egd"], color: "#EF4444", grades: LA },
  { id: "egd.barretts",   name: "Barrett's esophagus", tier: "lesion", procedures: ["egd"], color: "#F59E0B", note: "extent = Prague C&M" },
  { id: "egd.varices",    name: "Esophageal/gastric varices", tier: "lesion", procedures: ["egd"], color: "#7C3AED" },
  { id: "egd.eso_cancer", name: "Esophageal cancer",   tier: "lesion", procedures: ["egd"], color: "#B91C1C", grades: PARIS },
  { id: "egd.hiatal",     name: "Hiatal hernia",       tier: "lesion", procedures: ["egd"], color: "#A855F7" },
  { id: "egd.mallory",    name: "Mallory-Weiss tear",  tier: "lesion", procedures: ["egd"], color: "#DC2626" },
  { id: "egd.gastric_ulcer", name: "Gastric ulcer",    tier: "lesion", procedures: ["egd"], color: "#DC2626", grades: FORREST },
  { id: "egd.duodenal_ulcer", name: "Duodenal ulcer",  tier: "lesion", procedures: ["egd"], color: "#DC2626", grades: FORREST },
  { id: "egd.gastritis",  name: "Gastritis / erythema", tier: "lesion", procedures: ["egd"], color: "#FB7185" },
  { id: "egd.gastric_polyp", name: "Gastric polyp",    tier: "lesion", procedures: ["egd"], color: "#EF4444", grades: PARIS },
  { id: "egd.gastric_cancer", name: "Gastric cancer",  tier: "lesion", procedures: ["egd"], color: "#991B1B", grades: PARIS },
  { id: "egd.gave",       name: "GAVE (watermelon stomach)", tier: "lesion", procedures: ["egd"], color: "#EC4899" },
  { id: "egd.duodenitis", name: "Duodenitis",          tier: "lesion", procedures: ["egd"], color: "#FB923C" },
  { id: "egd.angiodysplasia", name: "Angiodysplasia",  tier: "lesion", procedures: ["egd"], color: "#EC4899" },
  { id: "egd.bleeding",   name: "Active bleeding",     tier: "lesion", procedures: ["egd"], color: "#991B1B" },
];

export const TAXONOMY: Taxon[] = [
  ...COLON_LANDMARKS, ...COLON_LESIONS, ...EGD_LANDMARKS, ...EGD_LESIONS,
];

export function taxaFor(procedure: Procedure, tier: Tier): Taxon[] {
  return TAXONOMY.filter((t) => t.tier === tier && t.procedures.includes(procedure));
}
export function taxonById(id: string | undefined): Taxon | undefined {
  return id ? TAXONOMY.find((t) => t.id === id) : undefined;
}
